import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { catalog } from '../engine/catalog';
import { EffectCard } from '../components/EffectCard';
import { AdSlot } from '../components/AdSlot';
import { categoryIcon, CATEGORY_VAR } from '../components/icons';

export function EffectsPage() {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const q = query.trim().toLowerCase();
  const presets = useMemo(
    () => catalog.presets.filter((p) => !q || p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)),
    [q],
  );
  const tools = catalog.tools.filter((t) => !q || t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q));
  const ToolsIcon = categoryIcon('tools');

  return (
    <div className="container" style={{ paddingTop: 32 }}>
      <div className="section__head">
        <div>
          <h1 style={{ fontSize: 32 }}>Voice effects library</h1>
          <p className="muted" style={{ margin: 0 }}>
            {catalog.presets.length} voice presets and {catalog.tools.length} enhancement tools. All are DSP effects
            (pitch, formant, filters, dynamics, modulation, reverb) — not AI voice cloning.
          </p>
        </div>
        <label style={{ minWidth: 260, position: 'relative' }}>
          <span className="sr-only">Search effects</span>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 12, color: 'var(--c-text-muted)' }} />
          <input type="search" placeholder="Search effects…" value={query} onChange={(e) => setQuery(e.target.value)} style={{ paddingLeft: 36 }} />
        </label>
      </div>

      {catalog.categories.filter((c) => c.kind !== 'tool').map((c) => {
        const items = presets.filter((p) => p.category === c.id);
        if (items.length === 0) return null;
        const Icon = categoryIcon(c.id);
        return (
          <section key={c.id} id={c.id} className="category-block">
            <div className="category-block__head">
              <span className="effect-card__icon" style={{ ['--cat' as string]: CATEGORY_VAR(c.id) }}><Icon size={18} /></span>
              <h2 style={{ margin: 0 }}>{c.name}</h2>
              <span className="badge">{items.length}</span>
            </div>
            {c.note && <p className="category-block__note">{c.note}</p>}
            <div className="effect-grid">
              {items.map((p) => <EffectCard key={p.id} preset={p} onSelect={() => navigate(`/editor?preset=${p.id}`)} />)}
            </div>
          </section>
        );
      })}

      {tools.length > 0 && (
        <section id="tools" className="category-block">
          <div className="category-block__head">
            <span className="effect-card__icon" style={{ ['--cat' as string]: CATEGORY_VAR('tools') }}><ToolsIcon size={18} /></span>
            <h2 style={{ margin: 0 }}>Audio enhancement tools</h2>
            <span className="badge">{tools.length}</span>
          </div>
          <p className="category-block__note">These clean-up tools live in the editor's Enhance tab and combine with any voice effect.</p>
          <div className="effect-grid">
            {tools.map((t) => (
              <div key={t.id} className="effect-card" style={{ ['--cat' as string]: CATEGORY_VAR('tools'), cursor: 'default' }}>
                <span className="effect-card__name">{t.name}</span>
                <span className="effect-card__desc">{t.description}</span>
              </div>
            ))}
          </div>
        </section>
      )}
      {presets.length === 0 && tools.length === 0 && <p className="muted">No effects match “{query}”.</p>}

      <AdSlot slot="library" />
    </div>
  );
}
